import { normalizeCatalogSourcePhase } from './catalogSourcePhase';

function normalizedStatus(value) {
  const status = String(value || '').trim().toLowerCase();
  if (['success', 'completed', 'complete', 'done', 'ready'].includes(status)) return 'completed';
  if (['running', 'in_progress', 'processing'].includes(status)) return 'running';
  if (['scheduled', 'queued', 'pending'].includes(status)) return 'queued';
  if (['error', 'failed', 'failure'].includes(status)) return 'failed';
  return status || 'idle';
}

function firstNumber(...values) {
  return values.find(value => typeof value === 'number' && Number.isFinite(value));
}

function normalizeJob(job) {
  if (!job || typeof job !== 'object') return null;
  const status = normalizedStatus(job.status);
  const indexedCount = firstNumber(job.indexed_count, job.indexedCount, job.stats?.itemsProcessed);
  const totalCount = firstNumber(
    job.total_count,
    job.totalCount,
    job.stats?.itemsTotal,
    job.stats?.totalItems,
  );
  const progress = firstNumber(job.progress, job.stats?.progress)
    ?? (status === 'completed' ? 100 : null);

  return {
    ...normalizeCatalogSourcePhase({ ...job, status }),
    jobId: job.job_id || job.jobId || job.taskId || job._id || null,
    status,
    progress,
    indexedCount,
    totalCount,
    createdAt: job.created_at || job.createdAt || null,
    startedAt: job.started_at || job.startedAt || null,
    finishedAt: job.finished_at || job.finishedAt || null,
    // Some crawler transports return a success summary in `message`. Treat it
    // as an error only for failed jobs so the UI can localize successful
    // outcomes instead of leaking transport copy such as "Catalog update completed".
    error: job.error || (status === 'failed' ? job.message : null) || null,
  };
}

function pluginStateJob(response) {
  const indexing = response?.state?.indexing;
  if (!indexing || typeof indexing !== 'object') return null;
  return normalizeJob(indexing);
}

export function normalizeIndexingSnapshot(historyResponse, statusResponse = null) {
  const history = historyResponse?.data?.history || historyResponse?.history || [];
  const historyJobs = Array.isArray(history) ? history.map(normalizeJob).filter(Boolean) : [];
  const historyCurrent = normalizeJob(historyResponse?.current);
  const statusCurrent = normalizeJob(statusResponse?.current) || pluginStateJob(statusResponse);
  // History may carry the same job before the worker enters or leaves source
  // preparation. Its catalog metrics remain useful; status owns the live phase.
  const current = historyCurrent && statusCurrent && historyCurrent.jobId === statusCurrent.jobId
    ? {
      ...historyCurrent,
      phase: statusCurrent.phase,
      sourceProcessed: statusCurrent.sourceProcessed,
      sourceTotal: statusCurrent.sourceTotal,
    }
    : historyCurrent || statusCurrent;
  const pluginIndexing = statusResponse?.state?.indexing || {};
  const lastSuccessAt = historyResponse?.lastSuccessAt
    || statusResponse?.lastSuccessAt
    || pluginIndexing.last_success_at
    || pluginIndexing.last_successful_at
    || pluginIndexing.last_completed_at
    || null;

  return {
    current: current && ['running', 'queued'].includes(current.status) ? current : null,
    history: historyJobs,
    lastSuccessAt,
    lastErrorAt: historyResponse?.lastErrorAt || statusResponse?.lastErrorAt || null,
  };
}

export function normalizeStartedJob(response) {
  const explicitJob = response?.task || response?.job;
  const bridgeJob = !explicitJob && (response?.job_id || response?.jobId) ? response : null;
  const normalized = normalizeJob(explicitJob || bridgeJob);
  if (normalized) {
    return {
      ...normalized,
      status: ['running', 'queued'].includes(normalized.status) ? normalized.status : 'queued',
    };
  }
  return {
    jobId: response?.job_id || response?.jobId || null,
    status: 'queued',
    progress: null,
    indexedCount: null,
    totalCount: null,
    createdAt: new Date().toISOString(),
    startedAt: null,
    finishedAt: null,
    error: null,
  };
}

export function isIndexingActive(job) {
  return Boolean(job && ['running', 'queued'].includes(normalizedStatus(job.status)));
}

export { normalizedStatus };
